/**
 * "Ask Conway" from the Go box. With the dock open AND mounted on this route
 * the text is parked in its composer — the dock never auto-sends (see
 * `useChatHandoff`), and one keypress from sent is the honest state.
 * Otherwise a new conversation starts with the text as its first message and
 * the Chat tab shows it.
 *
 * `open` alone is not enough: it is persisted and stays true on routes where
 * the dock does not render (the Chat page excludes it). `bodyOpen` is written
 * only by a mounted dock column, so `open && bodyOpen` means "visible now".
 *
 * With a retained selection (the context object), the question is sent with
 * the selection quoted above it.
 */
import { composeSelectionPrompt } from '@shared/active-context';
import { useChatDockStore } from '@shared/components/chat/dock/useChatDockStore';
import { useChatHandoffStore } from '@shared/components/chat/dock/useChatHandoff';

import { startConversation } from './startConversation';

/** Where the question went. */
export type AskOutcome = 'dock' | 'chat' | 'ignored';

/**
 * Route a Go-box question to the open dock's composer or to a new conversation.
 *
 * @param text - What the user typed.
 * @param openChat - Opens or focuses the Chat tab; the caller owns navigation.
 * @param selection - The context object's retained selection; quoted ahead of the question.
 */
export function askConway(
  text: string,
  openChat: () => void,
  selection: string | null = null
): AskOutcome {
  const trimmed = text.trim();
  if (!trimmed) return 'ignored';
  const message = selection ? composeSelectionPrompt(selection, trimmed) : trimmed;
  const dock = useChatDockStore.getState();
  if (dock.open && dock.bodyOpen) {
    useChatHandoffStore.getState().request(message);
    return 'dock';
  }
  startConversation(message);
  openChat();
  return 'chat';
}
